import { useCallback, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuthModal } from '../providers/auth-modal.provider';
import { usePurchaseReminder } from '../providers/purchase-reminder.provider';
import { useUserState } from '../providers/user-state.provider.ts';
import { createVideoCheckout } from '../services/purchase.service';
import { getSubscriptionDetails } from '../services/payment.service';
import { isSubscriptionActive } from '../utils/subscription';
import { createAnalyticsClientEventId } from '../services/analytics.service';

const resolveHasActiveSubscription = async (token: string): Promise<boolean> => {
    try {
        const subscription = await getSubscriptionDetails(token);
        return isSubscriptionActive(subscription);
    } catch (error) {
        return false;
    }
};

export const usePurchaseFlow = () => {
    const { getUserInfo } = useUserState();
    const { openModal } = useAuthModal();
    const { openReminder } = usePurchaseReminder();
    const [isProcessing, setIsProcessing] = useState(false);
    const checkoutInFlightRef = useRef(false);
    const purchaseInFlightRef = useRef(false);

    const startCheckout = useCallback(async (videoId: string, tokenOverride?: string) => {
        if (!videoId) {
            return;
        }

        if (checkoutInFlightRef.current) {
            return;
        }

        const userInfo = getUserInfo();
        const token = tokenOverride ?? userInfo?.token;
        if (!token) {
            localStorage.setItem('pendingVideoPurchaseId', videoId);
            localStorage.setItem('redirectAfterLogin', window.location.href);
            openModal('paid_content', window.location.href);
            return;
        }

        checkoutInFlightRef.current = true;
        setIsProcessing(true);
        try {
            const checkoutEventId = createAnalyticsClientEventId();
            const response = await createVideoCheckout(videoId, token, checkoutEventId);
            if (response.RedirectUrl) {
                window.location.href = response.RedirectUrl;
                return;
            }
            if (response.CheckoutUrl) {
                window.location.href = response.CheckoutUrl;
                return;
            }
            if (response.Message) {
                toast.error(response.Message);
            } else {
                toast.error('Unable to start purchase.');
            }
        } catch (error) {
            toast.error('Unable to start purchase.');
        } finally {
            checkoutInFlightRef.current = false;
            setIsProcessing(false);
        }
    }, [getUserInfo, openModal]);

    const startPurchase = useCallback(async (videoId: string, tokenOverride?: string) => {
        if (!videoId) {
            return;
        }

        if (purchaseInFlightRef.current) {
            return;
        }

        const userInfo = getUserInfo();
        const token = tokenOverride ?? userInfo?.token;
        if (!token) {
            localStorage.setItem('pendingVideoPurchaseId', videoId);
            localStorage.setItem('redirectAfterLogin', window.location.href);
            openModal('paid_content', window.location.href);
            return;
        }

        purchaseInFlightRef.current = true;
        setIsProcessing(true);
        try {
            const hasActiveSubscription = await resolveHasActiveSubscription(token);
            if (!hasActiveSubscription) {
                openReminder(videoId);
                return;
            }

            await startCheckout(videoId, token);
        } finally {
            purchaseInFlightRef.current = false;
            setIsProcessing(false);
        }
    }, [getUserInfo, openModal, openReminder, startCheckout]);

    return {
        startPurchase,
        startCheckout,
        isProcessing
    };
};
